import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Grid,
  Paper,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BadgeIcon from '@mui/icons-material/Badge';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, UserRole } from '../types';
import { userService, UserCreateInput, UserUpdateInput } from '../services/user.service';
import authService from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/common/PageHeader';
import { toTitleCase } from '../utils/formatters';

// Canonical assignable roles. SUPER_ADMIN appears only for existing super admins.
const BASE_ROLE_OPTIONS = [
  { value: UserRole.ADMIN,  label: 'Admin',  description: 'Full access (below Super Admin)', color: '#DC2626', bg: '#FEE2E2' },
  { value: UserRole.HR,     label: 'HR',     description: 'Employees, Attendance, Salary Slips', color: '#7C3AED', bg: '#EDE9FE' },
  { value: UserRole.DEALER, label: 'Dealer', description: 'Dashboard, Trades, Market, Risk, Clients', color: '#2563EB', bg: '#DBEAFE' },
];
const SUPER_ADMIN_OPTION = { value: UserRole.SUPER_ADMIN, label: 'Super Admin', description: 'Full system access', color: '#B45309', bg: '#FEF3C7' };

// Pill for any role (including legacy ones). Uses BASE_ROLE_OPTIONS colors
// where possible; falls back to slate.
function roleChipStyle(role: UserRole): { color: string; bg: string; label: string } {
  const all = [...BASE_ROLE_OPTIONS, SUPER_ADMIN_OPTION];
  const match = all.find((r) => r.value === role);
  if (match) return { color: match.color, bg: match.bg, label: match.label };
  // Legacy
  if (role === UserRole.AGENT)    return { color: '#2563EB', bg: '#DBEAFE', label: 'Dealer' }; // treat legacy agent visually as Dealer
  if (role === UserRole.CLIENT)   return { color: '#059669', bg: '#D1FAE5', label: 'Client' };
  if (role === UserRole.EMPLOYEE) return { color: '#64748B', bg: '#F1F5F9', label: 'Employee' };
  return { color: '#64748B', bg: '#F1F5F9', label: String(role) };
}

const userSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  roles: z.array(z.nativeEnum(UserRole)).min(1, 'At least one role is required'),
  isActive: z.boolean().optional(),
});

type UserFormData = z.infer<typeof userSchema>;

export function UserManagement() {
  const { isSuperAdmin } = useAuth();
  const roleOptions = useMemo(
    () => (isSuperAdmin ? [SUPER_ADMIN_OPTION, ...BASE_ROLE_OPTIONS] : BASE_ROLE_OPTIONS),
    [isSuperAdmin]
  );

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [resendingFor, setResendingFor] = useState<string | null>(null);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: { name: '', email: '', roles: [UserRole.DEALER], isActive: true },
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Derived
  const filteredUsers = useMemo(() => {
    let list = users;
    if (statusFilter === 'active') list = list.filter((u) => u.isActive);
    if (statusFilter === 'inactive') list = list.filter((u) => !u.isActive);
    if (roleFilter !== 'all') {
      list = list.filter((u) => {
        const r = u.roles || (u.role ? [u.role] : []);
        // Treat AGENT as DEALER for filtering
        if (roleFilter === UserRole.DEALER) return r.includes(UserRole.DEALER) || r.includes(UserRole.AGENT);
        return r.includes(roleFilter);
      });
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(
        (u) =>
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          (u.roles || []).join(' ').toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, statusFilter, roleFilter, searchText]);

  const hasRole = (u: User, role: UserRole) => {
    const r = u.roles || (u.role ? [u.role] : []);
    return r.includes(role);
  };

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.isActive).length,
    admins: users.filter((u) => hasRole(u, UserRole.ADMIN)).length,
    hr: users.filter((u) => hasRole(u, UserRole.HR)).length,
    // Count both DEALER (canonical) and legacy AGENT as "dealers"
    dealers: users.filter((u) => hasRole(u, UserRole.DEALER) || hasRole(u, UserRole.AGENT)).length,
  }), [users]);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      let userRoles = user.roles || (user.role ? [user.role] : []);
      // Display legacy AGENT as DEALER in the form for consistency.
      userRoles = userRoles.map((r) => (r === UserRole.AGENT ? UserRole.DEALER : r));
      // Drop roles not shown in the current role-option list (e.g. CLIENT/EMPLOYEE
      // when editing a staff user — they wouldn't be present, but guard anyway).
      const allowed = roleOptions.map((r) => r.value);
      const filteredRoles = userRoles.filter((r) => allowed.includes(r));
      reset({
        name: user.name,
        email: user.email,
        roles: filteredRoles.length > 0 ? filteredRoles : [UserRole.DEALER],
        isActive: user.isActive,
      });
    } else {
      setEditingUser(null);
      reset({ name: '', email: '', roles: [UserRole.DEALER], isActive: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        const updateData: UserUpdateInput = { name: data.name, roles: data.roles, isActive: data.isActive };
        await userService.updateUser(editingUser.userId, updateData);
        setSuccess('User updated successfully');
      } else {
        const createData: UserCreateInput = {
          email: data.email,
          name: data.name,
          roles: data.roles,
          isActive: data.isActive ?? true,
        };
        await userService.createUser(createData);
        setSuccess('User created. They can log in via OTP (no password required).');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await userService.deleteUser(userToDelete.userId);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userService.toggleUserStatus(user.userId, !user.isActive);
      setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const handleResendOTP = async (user: User) => {
    setResendingFor(user.userId);
    try {
      await authService.sendOTP({ email: user.email, purpose: 'signup' });
      setSuccess(`OTP sent to ${user.email}`);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setResendingFor(null);
    }
  };

  // ---------- KPI + toolbar ----------

  const kpis: { label: string; value: string; icon: React.ReactNode; accent: string; accentBg: string }[] = [
    { label: 'Total Users',  value: stats.total.toString(),   icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />,      accent: '#4F46E5', accentBg: '#EEF2FF' },
    { label: 'Active',       value: stats.active.toString(),  icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />, accent: '#059669', accentBg: '#D1FAE5' },
    { label: 'Admins',       value: stats.admins.toString(),  icon: <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />, accent: '#DC2626', accentBg: '#FEE2E2' },
    { label: 'HR',           value: stats.hr.toString(),      icon: <BadgeIcon sx={{ fontSize: 20 }} />,              accent: '#7C3AED', accentBg: '#EDE9FE' },
    { label: 'Dealers',      value: stats.dealers.toString(), icon: <SupportAgentIcon sx={{ fontSize: 20 }} />,       accent: '#2563EB', accentBg: '#DBEAFE' },
  ];

  // ---------- Column defs ----------

  const columnDefs: ColDef<User>[] = [
    {
      field: 'name',
      headerName: 'User',
      flex: 1.5,
      minWidth: 220,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const u = params.data;
        return (
          <Box display="flex" alignItems="center" gap={1.25} sx={{ py: 0.5 }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1.25,
                bgcolor: '#EEF2FF', color: '#4F46E5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.82rem', letterSpacing: '-0.01em', flexShrink: 0,
              }}
            >
              {(params.value || 'U').charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0F172A', lineHeight: 1.25 }}>
                {toTitleCase(params.value || '')}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u?.email}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1.5,
      minWidth: 220,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const u = params.data;
        const rolesArr = u?.roles || (u?.role ? [u.role] : []);
        // Dedup AGENT→DEALER for display
        const display = Array.from(new Set(rolesArr.map((r) => (r === UserRole.AGENT ? UserRole.DEALER : r))));
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
            {display.map((r, i) => {
              const s = roleChipStyle(r);
              return (
                <Chip
                  key={i}
                  label={s.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: s.bg,
                    color: s.color,
                    letterSpacing: '0.02em',
                  }}
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const active = !!params.value;
        return (
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: active ? '#10B981' : '#94A3B8',
                boxShadow: active ? '0 0 0 3px rgba(16, 185, 129, 0.15)' : 'none',
              }}
            />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: active ? '#047857' : '#64748B' }}>
              {active ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'isVerified',
      headerName: 'Verified',
      width: 110,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const verified = !!params.value;
        return (
          <Chip
            label={verified ? 'Yes' : 'Pending'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 700,
              bgcolor: verified ? '#D1FAE5' : '#FEF3C7',
              color: verified ? '#047857' : '#B45309',
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      cellRenderer: (params: ICellRendererParams<User>) => (
        <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 500, fontFeatureSettings: '"tnum"' }}>
          {params.value ? new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
        </Typography>
      ),
    },
    {
      headerName: 'Actions',
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<User>) => {
        const u = params.data;
        if (!u) return null;
        return (
          <Box display="flex" gap={0.5} alignItems="center" sx={{ py: 0.5 }}>
            <Tooltip title="Edit user">
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(u)}
                sx={{
                  color: '#64748B',
                  border: '1px solid #E2E8F0',
                  borderRadius: 1,
                  width: 28, height: 28,
                  '&:hover': { color: '#4F46E5', borderColor: '#4F46E5', bgcolor: '#EEF2FF' },
                }}
              >
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Resend OTP / invite">
              <span>
                <IconButton
                  size="small"
                  disabled={resendingFor === u.userId}
                  onClick={() => handleResendOTP(u)}
                  sx={{
                    color: '#64748B',
                    border: '1px solid #E2E8F0',
                    borderRadius: 1,
                    width: 28, height: 28,
                    '&:hover': { color: '#7C3AED', borderColor: '#7C3AED', bgcolor: '#EDE9FE' },
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
              <IconButton
                size="small"
                onClick={() => handleToggleStatus(u)}
                sx={{
                  color: u.isActive ? '#D97706' : '#059669',
                  border: `1px solid ${u.isActive ? '#FDE68A' : '#A7F3D0'}`,
                  borderRadius: 1,
                  width: 28, height: 28,
                  '&:hover': { bgcolor: u.isActive ? '#FEF3C7' : '#D1FAE5' },
                }}
              >
                {u.isActive ? <BlockIcon sx={{ fontSize: 15 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(u)}
                sx={{
                  color: '#DC2626',
                  border: '1px solid #FECACA',
                  borderRadius: 1,
                  width: 28, height: 28,
                  '&:hover': { bgcolor: '#FEE2E2' },
                }}
              >
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Administration"
        title="User Management"
        subtitle="Add team members, assign roles, and control who can access the system."
        actions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => handleOpenDialog()}
            sx={{
              px: 2,
              py: 0.85,
              borderRadius: 1.5,
              bgcolor: '#4F46E5',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              boxShadow: '0 2px 6px rgba(79, 70, 229, 0.22)',
              '&:hover': { bgcolor: '#3730A3', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.32)' },
            }}
          >
            Add User
          </Button>
        }
      />

      {/* KPIs */}
      <Grid container spacing={2.5} mb={3}>
        {kpis.map((k) => (
          <Grid item xs={6} md={2.4} key={k.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                transition: 'all 0.2s ease',
                height: '100%',
                '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)' },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                <Typography
                  sx={{
                    fontWeight: 600, color: '#64748B',
                    letterSpacing: '0.04em', fontSize: '0.68rem', textTransform: 'uppercase',
                  }}
                >
                  {k.label}
                </Typography>
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: 1.5,
                    bgcolor: k.accentBg, color: k.accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {k.icon}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: '1.5rem', fontWeight: 700, color: '#0F172A',
                  letterSpacing: '-0.02em', fontFeatureSettings: '"tnum"', lineHeight: 1.2,
                }}
              >
                {k.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          mb: 2, p: 1.25, borderRadius: 2, bgcolor: '#FFFFFF',
          border: '1px solid #E2E8F0', display: 'flex', flexWrap: 'wrap',
          alignItems: 'center', gap: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name, email, role…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 240, flex: '1 1 260px', maxWidth: 380,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#F8FAFC', borderRadius: 1.5, fontSize: '0.85rem',
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#4F46E5', borderWidth: 1 },
            },
          }}
        />

        <Box sx={{ width: '1px', height: 24, bgcolor: '#E2E8F0', mx: 0.5 }} />

        <Box sx={{ display: 'inline-flex', p: 0.375, bgcolor: '#F1F5F9', borderRadius: 1.5, gap: 0.25 }}>
          {(['all', 'active', 'inactive'] as const).map((m) => {
            const active = statusFilter === m;
            const label = m === 'all' ? 'All' : m === 'active' ? 'Active' : 'Inactive';
            return (
              <Box
                key={m}
                onClick={() => setStatusFilter(m)}
                sx={{
                  px: 1.5, py: 0.625, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  color: active ? '#0F172A' : '#64748B',
                  bgcolor: active ? '#FFFFFF' : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: '#0F172A' },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            displayEmpty
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'all' | UserRole)}
            sx={{
              bgcolor: '#F8FAFC', borderRadius: 1.5,
              fontSize: '0.82rem', fontWeight: 600, color: '#0F172A',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4F46E5' },
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {roleOptions.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Chip
          label={`${filteredUsers.length} of ${users.length} shown`}
          size="small"
          sx={{
            fontSize: '0.72rem', fontWeight: 600,
            bgcolor: '#EEF2FF', color: '#3730A3',
            fontFeatureSettings: '"tnum"',
          }}
        />
      </Paper>

      {/* Grid */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Box
          className="ag-theme-alpine"
          sx={{
            height: 560, width: '100%',
            '--ag-background-color': '#FFFFFF',
            '--ag-odd-row-background-color': '#FFFFFF',
            '--ag-header-background-color': '#F8FAFC',
            '--ag-header-foreground-color': '#64748B',
            '--ag-row-hover-color': '#F8FAFC',
            '--ag-selected-row-background-color': '#EEF2FF',
            '--ag-border-color': '#E2E8F0',
            '--ag-row-border-color': '#E2E8F0',
            '--ag-font-family': 'Inter, -apple-system, system-ui, sans-serif',
            '--ag-font-size': '13px',
            '--ag-grid-size': '4px',
            '& .ag-header': { borderBottom: '1px solid #E2E8F0' },
            '& .ag-header-cell': { fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
            '& .ag-header-cell-label': { fontWeight: 600, color: '#64748B' },
            '& .ag-row': {
              borderBottom: '1px solid #F1F5F9',
              transition: 'background-color 0.12s ease',
            },
            '& .ag-cell': { display: 'flex', alignItems: 'center', color: '#0F172A' },
            '& .ag-root-wrapper': { border: 'none' },
            '& .ag-overlay-loading-center, & .ag-overlay-no-rows-center': {
              fontSize: '0.85rem', color: '#64748B', fontWeight: 500,
              bgcolor: 'transparent', boxShadow: 'none', border: 'none',
            },
          }}
        >
          <AgGridReact<User>
            rowData={filteredUsers}
            columnDefs={columnDefs}
            defaultColDef={{ sortable: true, filter: false, resizable: true }}
            animateRows
            rowHeight={56}
            headerHeight={40}
            pagination
            paginationPageSize={25}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading users…</span>'
            overlayNoRowsTemplate={loading ? '<span class="ag-overlay-no-rows-center">Loading users…</span>' : '<span class="ag-overlay-no-rows-center">No users match your filters</span>'}
          />
        </Box>
      </Paper>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ fontWeight: 700, color: '#0F172A', borderBottom: '1px solid #E2E8F0' }}>
            {editingUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    fullWidth
                    size="small"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    autoFocus
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    fullWidth
                    size="small"
                    disabled={!!editingUser}
                    error={!!errors.email}
                    helperText={errors.email?.message || (editingUser ? 'Email cannot be changed' : 'First login will be via OTP to this address')}
                  />
                )}
              />

              <Controller
                name="roles"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.roles}>
                    <InputLabel>Roles</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Roles"
                      value={field.value || []}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as UserRole[]).map((value) => {
                            const s = roleChipStyle(value);
                            return (
                              <Chip
                                key={value}
                                label={s.label}
                                size="small"
                                sx={{ height: 22, bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.7rem' }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {roleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox checked={(field.value as UserRole[] || []).indexOf(option.value) > -1} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{option.label}</Typography>
                            <Typography variant="caption" color="#64748B">{option.description}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.roles && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.roles.message as string}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="isActive"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#0F172A">Active</Typography>
                        <Typography variant="caption" color="#64748B">
                          {value ? 'User can sign in' : 'User is blocked from signing in'}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {!editingUser && (
                <Alert severity="info" sx={{ borderRadius: 1.5, fontSize: '0.82rem' }}>
                  The user will receive a one-time code by email the first time they try to sign in.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid #E2E8F0', pt: 1.5 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: '#4F46E5',
                '&:hover': { bgcolor: '#3730A3' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 2.5,
              }}
            >
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete user?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem' }}>
            <strong>{userToDelete?.name}</strong> ({userToDelete?.email}) will be marked inactive and prevented from signing in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{ bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C' }, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
    </Box>
  );
}

export default UserManagement;
